<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  contact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const isSaving = ref(false);
const isEditing = ref(false);
const processesList = ref([]);
const originalProcesses = ref([]);

const STATUS_OPTIONS = [
  'Em andamento',
  'Fase de recurso',
  'Ganho',
  'Pago',
  'Perdido',
  'Cancelado',
  'Arquivado',
];

const formatProcessDate = (dateStr) => {
  if (!dateStr) return 'Não informada';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const getStatusBadgeClass = (status) => {
  const s = (status || '').toLowerCase().trim();
  if (s.includes('andamento')) {
    return 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60';
  }
  if (s.includes('ganho') || s.includes('pago') || s.includes('conclu')) {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60';
  }
  if (s.includes('recurso') || s.includes('viabilidade') || s.includes('pendente')) {
    return 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60';
  }
  if (s.includes('perdido') || s.includes('cancelado') || s.includes('arquivado')) {
    return 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700';
};

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      const existing = props.contact?.additional_attributes?.processes || [];
      originalProcesses.value = JSON.parse(JSON.stringify(existing));
      processesList.value = JSON.parse(JSON.stringify(existing));
      isEditing.value = false;
    }
  },
  { immediate: true }
);

const startEditing = () => {
  isEditing.value = true;
};

const cancelEditing = () => {
  processesList.value = JSON.parse(JSON.stringify(originalProcesses.value));
  isEditing.value = false;
};

const addNewProcess = () => {
  isEditing.value = true;
  processesList.value.push({
    parties: '',
    court: '',
    type: '',
    number: '',
    url: '',
    lawyers: '',
    date: new Date().toISOString().split('T')[0],
    status: 'Em andamento',
    value: '',
  });
};

const removeProcess = (index) => {
  processesList.value.splice(index, 1);
};

const handleClose = () => {
  if (isEditing.value) {
    cancelEditing();
  }
  emit('close');
};

const copyProcessNumber = (number) => {
  if (!number) return;
  navigator.clipboard.writeText(number);
  useAlert('Número do processo copiado!');
};

const saveProcesses = async () => {
  isSaving.value = true;
  try {
    const validProcesses = processesList.value.filter(
      p => p.type?.trim() || p.number?.trim() || p.parties?.trim() || p.court?.trim() || p.url?.trim() || p.value?.trim() || p.lawyers?.trim() || p.date || p.status
    );

    const currentAdditional = props.contact?.additional_attributes || {};
    const updatedAdditional = {
      ...currentAdditional,
      processes: validProcesses,
    };

    await store.dispatch('contacts/update', {
      id: props.contact.id,
      additional_attributes: updatedAdditional,
    });

    originalProcesses.value = JSON.parse(JSON.stringify(validProcesses));
    processesList.value = JSON.parse(JSON.stringify(validProcesses));
    isEditing.value = false;

    useAlert('Processos salvos com sucesso!');
    emit('updated');
  } catch (err) {
    useAlert(err.message || 'Erro ao salvar processos.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Modal :show="show" :on-close="handleClose">
    <div class="p-6 max-h-[90vh] flex flex-col">
      <!-- HEADER DO MODAL -->
      <div class="flex items-center justify-between pb-3 border-b border-n-slate-4 dark:border-slate-800">
        <div>
          <h3 class="text-base font-bold text-n-slate-12 flex items-center gap-2">
            <span>⚖️</span>
            <span>Processos do Cliente</span>
          </h3>
          <p class="text-xs text-n-slate-10 mt-0.5">
            {{ contact?.name ? `Processos judiciais vinculados a ${contact.name}` : 'Gerenciamento de processos judiciais' }}
          </p>
        </div>

        <!-- BADGE / BOTÃO DE MODO NO TOPO -->
        <div class="flex items-center gap-2">
          <button
            v-if="!isEditing"
            type="button"
            class="px-2.5 py-1 rounded-lg text-xs font-semibold bg-n-brand text-white hover:brightness-110 shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
            @click="startEditing"
          >
            <span class="i-lucide-pencil size-3.5"></span>
            <span>Editar Processos</span>
          </button>
          
          <span
            v-else
            class="px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 dark:bg-blue-950/50 text-n-brand dark:text-blue-400 border border-blue-200 dark:border-blue-800 flex items-center gap-1.5"
          >
            <span class="i-lucide-pencil size-3.5"></span>
            <span>Modo Edição Ativo</span>
          </span>
        </div>
      </div>

      <!-- CORPO DA LISTA DE PROCESSOS COM SCROLL -->
      <div class="flex-1 overflow-y-auto pr-1 my-4 space-y-4 max-h-[60vh]">
        <!-- CASO 1: SEM PROCESSOS -->
        <div
          v-if="processesList.length === 0"
          class="p-8 text-center border border-dashed border-n-slate-4 dark:border-slate-800 rounded-xl bg-n-alpha-black2"
        >
          <span class="text-3xl block mb-2">⚖️</span>
          <h4 class="text-sm font-semibold text-n-slate-12">Nenhum processo cadastrado</h4>
          <p class="text-xs text-n-slate-10 mt-1 mb-4">Cadastre o primeiro processo judicial para este cliente.</p>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-n-brand hover:brightness-110 text-white text-xs font-semibold rounded-lg shadow-sm transition-all cursor-pointer"
            @click="addNewProcess"
          >
            <span class="i-lucide-plus size-4"></span>
            <span>Cadastrar Processo</span>
          </button>
        </div>

        <!-- CASO 2: LISTA DE PROCESSOS -->
        <div
          v-for="(proc, index) in processesList"
          :key="index"
          class="p-4 border rounded-xl transition-all shadow-xs"
          :class="isEditing 
            ? 'border-n-brand/40 bg-blue-50/15 dark:bg-blue-950/20 dark:border-blue-900/40' 
            : 'border-n-slate-4 dark:border-slate-800 bg-n-alpha-black2 dark:bg-slate-900/40'"
        >
          <!-- HEADER DO ITEM DE PROCESSO -->
          <div class="flex items-center justify-between pb-2 mb-3 border-b border-n-slate-4 dark:border-slate-800/80">
            <span class="text-xs font-bold text-n-brand dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
              <span>⚖️</span>
              <span>Processo #{{ index + 1 }}</span>
            </span>

            <div class="flex items-center gap-2">
              <a
                v-if="!isEditing && proc.url"
                :href="proc.url"
                target="_blank"
                rel="noopener noreferrer"
                class="px-2 py-0.5 text-[11px] font-semibold text-n-brand hover:underline bg-blue-50 dark:bg-blue-950/40 rounded border border-blue-200 dark:border-blue-800/60 flex items-center gap-1 transition-colors"
                title="Abrir processo no tribunal"
              >
                <span class="i-lucide-external-link size-3"></span>
                <span>Tribunal 🔗</span>
              </a>

              <button
                v-if="!isEditing && proc.number"
                type="button"
                class="px-2 py-0.5 text-[11px] font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-black2 rounded border border-n-slate-3 hover:border-n-slate-4 flex items-center gap-1 transition-colors cursor-pointer"
                title="Copiar número do processo"
                @click="copyProcessNumber(proc.number)"
              >
                <span class="i-lucide-copy size-3"></span>
                <span>Copiar CNJ</span>
              </button>

              <button
                v-if="isEditing"
                type="button"
                class="p-1 rounded text-n-slate-10 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors cursor-pointer"
                title="Excluir este processo"
                @click="removeProcess(index)"
              >
                <span class="i-lucide-trash size-4"></span>
              </button>
            </div>
          </div>

          <!-- MODO VISUALIZAÇÃO COMPLETO (TODOS OS 8 CAMPOS COM ALTA LEGIBILIDADE) -->
          <div v-if="!isEditing" class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Partes Envolvidas</span>
              <span class="text-xs font-semibold text-n-slate-12 block break-words">
                {{ proc.parties || 'Não informado' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Vara / Comarca</span>
              <span class="text-xs font-medium text-n-slate-12 block break-words">
                {{ proc.court || 'Não informado' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Tipo da Ação</span>
              <span class="text-xs font-semibold text-n-slate-12 block break-words">
                {{ proc.type || 'Não informado' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Número do Processo (CNJ)</span>
              <span class="font-mono text-xs font-medium text-n-slate-12 block bg-n-alpha-black2 dark:bg-slate-900 px-2 py-1 rounded border border-n-slate-3 dark:border-slate-800 select-all">
                {{ proc.number || 'Não informado' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Advogado(s) Responsável(is)</span>
              <span class="text-xs font-semibold text-n-slate-12 block break-words">
                {{ proc.lawyers || proc.lawyer || proc.advogado || 'Não informado' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Data de Distribuição</span>
              <span class="text-xs font-medium text-n-slate-12 block">
                {{ formatProcessDate(proc.date) }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Situação e Fase</span>
              <span 
                class="inline-block px-2.5 py-0.5 rounded-md text-[11px] font-semibold tracking-wide"
                :class="getStatusBadgeClass(proc.status)"
              >
                {{ proc.status || 'Em andamento' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Valor da Causa / Condenação</span>
              <span class="text-xs font-semibold text-emerald-700 dark:text-emerald-400 block">
                {{ proc.value || 'Não informado' }}
              </span>
            </div>

            <div>
              <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Link Direto do Processo (URL)</span>
              <a
                v-if="proc.url"
                :href="proc.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs text-n-brand dark:text-blue-400 hover:underline break-all block"
              >
                {{ proc.url }} 🔗
              </a>
              <span v-else class="text-xs text-n-slate-10 italic block">Não informado</span>
            </div>
          </div>

          <!-- MODO EDIÇÃO (TODOS OS 8 CAMPOS HABILITADOS) -->
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Partes Envolvidas</label>
              <input
                v-model="proc.parties"
                type="text"
                placeholder="Ex: Autor x Réu / INSS"
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Vara / Comarca</label>
              <input
                v-model="proc.court"
                type="text"
                placeholder="Ex: 1ª Vara Cível - Foro de..."
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Tipo da Ação</label>
              <input
                v-model="proc.type"
                type="text"
                placeholder="Ex: Cumprimento de Sentença"
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Número do Processo (CNJ)</label>
              <input
                v-model="proc.number"
                type="text"
                placeholder="Ex: 1003538-65.2026.8.26.0032"
                class="w-full h-8 text-xs font-mono px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Advogado(s) Responsável(is)</label>
              <input
                v-model="proc.lawyers"
                type="text"
                placeholder="Ex: Vinicius de Marco Fiscarelli / Bruno Alves Roque"
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Data de Distribuição</label>
              <input
                v-model="proc.date"
                type="date"
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Situação e Fase</label>
              <select
                v-model="proc.status"
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand cursor-pointer"
              >
                <option v-for="opt in STATUS_OPTIONS" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Valor da Causa / Condenação</label>
              <input
                v-model="proc.value"
                type="text"
                placeholder="Ex: R$ 45.000,00"
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">Link Direto do Processo (URL)</label>
              <input
                v-model="proc.url"
                type="url"
                placeholder="Ex: https://esaj.tjsp.jus.br/..."
                class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>
          </div>
        </div>

        <!-- BOTÃO ADICIONAR NO MODO EDIÇÃO -->
        <button
          v-if="isEditing"
          type="button"
          class="w-full py-2.5 px-3 border border-dashed border-n-brand/60 dark:border-blue-500/60 rounded-xl text-xs font-semibold text-n-brand dark:text-blue-400 hover:bg-n-brand/5 dark:hover:bg-blue-950/30 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
          @click="addNewProcess"
        >
          <span class="i-lucide-plus size-4"></span>
          <span>+ Adicionar Outro Processo</span>
        </button>
      </div>

      <!-- FOOTER DE AÇÕES COM BOTÕES DESTACADOS -->
      <div class="flex items-center justify-between pt-3 border-t border-n-slate-4 dark:border-slate-800">
        <!-- LADO ESQUERDO: ADICIONAR PROCESSO -->
        <div>
          <button
            v-if="!isEditing"
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-n-brand dark:text-blue-400 hover:bg-n-brand/10 dark:hover:bg-blue-950/40 rounded-lg border border-n-brand/30 transition-colors cursor-pointer"
            @click="addNewProcess"
          >
            <span class="i-lucide-plus size-3.5"></span>
            <span>+ Adicionar Processo</span>
          </button>
        </div>

        <!-- LADO DIREITO: BOTÕES DE CONTROLE -->
        <div class="flex items-center gap-2.5">
          <!-- MODO VISUALIZAÇÃO -->
          <template v-if="!isEditing">
            <button
              type="button"
              class="px-3.5 py-1.5 text-xs font-semibold text-n-slate-12 bg-n-alpha-black2 hover:bg-n-alpha-black3 rounded-lg border border-n-slate-4 dark:border-slate-700 transition-colors cursor-pointer"
              @click="handleClose"
            >
              Fechar
            </button>
            <button
              type="button"
              class="px-3.5 py-1.5 text-xs font-semibold text-white bg-n-brand hover:brightness-110 rounded-lg shadow-sm flex items-center gap-1.5 transition-all cursor-pointer"
              @click="startEditing"
            >
              <span class="i-lucide-pencil size-3.5"></span>
              <span>Editar Processos</span>
            </button>
          </template>

          <!-- MODO EDIÇÃO -->
          <template v-else>
            <button
              type="button"
              class="px-3.5 py-1.5 text-xs font-semibold text-n-slate-12 bg-n-alpha-black2 hover:bg-n-alpha-black3 rounded-lg border border-n-slate-4 dark:border-slate-700 transition-colors cursor-pointer"
              :disabled="isSaving"
              @click="cancelEditing"
            >
              Cancelar
            </button>

            <button
              type="button"
              class="px-4 py-1.5 text-xs font-semibold text-white bg-n-brand hover:brightness-110 active:scale-[0.98] rounded-lg shadow-sm flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              :disabled="isSaving"
              @click="saveProcesses"
            >
              <span v-if="!isSaving" class="i-lucide-save size-3.5"></span>
              <span v-else class="i-lucide-loader-2 size-3.5 animate-spin"></span>
              <span>{{ isSaving ? 'Salvando...' : 'Salvar Alterações' }}</span>
            </button>
          </template>
        </div>
      </div>
    </div>
  </Modal>
</template>
